import { useEffect, useState } from 'react';
import { api, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Loading, Sheet, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';
import type { Asset } from './Library.tsx';

interface Stream { id: string; name: string; urlDisplay: string | null; providerId: string | null; providerName: string | null; providerStreamId: string | null; fallbackAssetId: string | null }
interface Provider {
  id: string; name: string; host: string; status: 'connected' | 'auth_failed' | 'expired' | 'unreachable'; lastCheckedAt: string; channelCount: number;
  account: { expiresAt?: string | null; maxConnections?: number | null; activeConnections?: number | null };
}
interface Channel { streamId: string; name: string; categoryName: string | null; icon: string | null; added: boolean }

const STATUS_LABEL: Record<Provider['status'], string> = { connected: 'Connected', auth_failed: 'Sign-in rejected', expired: 'Subscription expired', unreachable: 'Not reachable' };
const STATUS_TONE: Record<Provider['status'], string> = { connected: 'active', auth_failed: 'failed', expired: 'failed', unreachable: 'warning' };

/** Live HLS streams: direct links and IPTV provider channels (Xtream Codes), ADR-0023. */
export function Streams() {
  const org = useOrg();
  const toast = useToast();
  const streams = useApi<{ streams: Stream[] }>(orgPath(org, '/streams'), [org.id]);
  const providers = useApi<{ providers: Provider[] }>(orgPath(org, '/stream-providers'), [org.id]);
  const images = useApi<{ assets: Asset[] }>(orgPath(org, '/assets?status=ready'), [org.id]);
  const [sheet, setSheet] = useState<null | { kind: 'hls' } | { kind: 'connect' } | { kind: 'channels'; provider: Provider } | { kind: 'credentials'; provider: Provider }>(null);
  const manage = can(org, 'integrations.manage');
  const write = can(org, 'assets.write');
  const reload = () => {
    void streams.reload();
    void providers.reload();
  };
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      if (ok) toast(ok);
      reload();
      return true;
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
      return false;
    }
  };
  const fallbackImages = images.data?.assets.filter((a) => a.kind === 'image') ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Live streams</h1>
          <p>HLS channels for playlists. Players switch to a cached fallback image if a stream stops, and come back when it returns.</p>
        </div>
        <div className="row">
          {write && <button className="btn" onClick={() => setSheet({ kind: 'hls' })}><Icon name="plus" /> Add HLS link</button>}
          {manage && <button className="btn btn-primary" onClick={() => setSheet({ kind: 'connect' })}><Icon name="integrations" /> Connect IPTV provider</button>}
        </div>
      </div>

      <section className="card" aria-labelledby="prov-head" style={{ marginBottom: 16 }}>
        <div className="card-head"><h2 id="prov-head">IPTV providers</h2><span className="subtle">Xtream Codes accounts</span></div>
        {providers.error && <ErrorState error={providers.error} retry={providers.reload} />}
        {providers.loading && !providers.data && <Loading rows={1} />}
        {providers.data && !providers.data.providers.length && (
          <Empty title="No provider connected">Connect an IPTV subscription with its server address, username and password to add its live channels.</Empty>
        )}
        {!!providers.data?.providers.length && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 8 }}>
            {providers.data.providers.map((p) => (
              <li key={p.id} className="row" style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong>{p.name}</strong>
                  <div className="subtle">
                    {p.host} · {p.channelCount} channel{p.channelCount === 1 ? '' : 's'} added
                    {p.account.expiresAt ? ` · renews or expires ${new Date(p.account.expiresAt).toLocaleDateString()}` : ''}
                    {p.account.maxConnections ? ` · ${p.account.maxConnections} simultaneous screen${p.account.maxConnections === 1 ? '' : 's'}` : ''}
                  </div>
                </div>
                <Status value={STATUS_TONE[p.status]} label={STATUS_LABEL[p.status]} />
                <button className="btn btn-sm" onClick={() => setSheet({ kind: 'channels', provider: p })} disabled={p.status !== 'connected'}>Browse channels</button>
                {manage && (
                  <>
                    <button className="btn btn-sm btn-ghost" onClick={() => void run(() => api('POST', orgPath(org, `/stream-providers/${p.id}/check`)), 'Account checked')}>Re-check</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => setSheet({ kind: 'credentials', provider: p })}>Change password</button>
                    <button className="btn btn-sm btn-ghost" aria-label={`Remove ${p.name}`} onClick={() => confirm(`Remove ${p.name}? Its channels must be deleted first.`) && void run(() => api('DELETE', orgPath(org, `/stream-providers/${p.id}`)), 'Provider removed')}><Icon name="trash" size={16} /></button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card" aria-labelledby="streams-head">
        <div className="card-head"><h2 id="streams-head">Streams</h2><span className="subtle">Add them to a playlist like any media item</span></div>
        {streams.error && <ErrorState error={streams.error} retry={streams.reload} />}
        {streams.loading && !streams.data && <Loading />}
        {streams.data && !streams.data.streams.length && <Empty title="No streams yet">Add an HLS link or channels from a connected provider.</Empty>}
        {!!streams.data?.streams.length && (
          <table className="table">
            <thead><tr><th>Name</th><th>Source</th><th>Fallback</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {streams.data.streams.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td>
                  <td className="subtle">{s.providerId ? `${s.providerName} · channel ${s.providerStreamId}` : s.urlDisplay}</td>
                  <td className="subtle">{s.fallbackAssetId ? (images.data?.assets.find((a) => a.id === s.fallbackAssetId)?.name ?? 'Image') : 'None (skips to next item)'}</td>
                  <td>{write && <button className="btn btn-sm btn-ghost" aria-label={`Delete ${s.name}`} onClick={() => confirm(`Delete ${s.name}?`) && void run(() => api('DELETE', orgPath(org, `/streams/${s.id}`)), 'Stream deleted')}><Icon name="trash" size={16} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {sheet?.kind === 'hls' && <HlsSheet images={fallbackImages} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload(); }} />}
      {sheet?.kind === 'connect' && <ConnectSheet onClose={() => setSheet(null)} onSaved={(p) => { reload(); setSheet({ kind: 'channels', provider: p }); }} />}
      {sheet?.kind === 'credentials' && <CredentialsSheet provider={sheet.provider} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload(); }} />}
      {sheet?.kind === 'channels' && <ChannelsSheet provider={sheet.provider} images={fallbackImages} onClose={() => setSheet(null)} onAdded={reload} />}
    </>
  );
}

function FallbackSelect({ images, value, onChange }: { images: Asset[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="field">
      <label htmlFor="st-fallback">Fallback image</label>
      <select id="st-fallback" className="input" value={value} onChange={(e) => onChange(e.target.value)} aria-describedby="st-fallback-hint">
        <option value="">None</option>
        {images.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <span id="st-fallback-hint" className="subtle">Shown while the stream is down; recommended for every channel.</span>
    </div>
  );
}

function HlsSheet({ images, onClose, onSaved }: { images: Asset[]; onClose: () => void; onSaved: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', url: '', fallback: '' });
  const save = async () => {
    try {
      await api('POST', orgPath(org, '/streams'), { name: form.name, url: form.url, fallbackAssetId: form.fallback || null });
      toast('Stream added');
      onSaved();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  };
  return (
    <Sheet title="Add HLS link" subtitle="An https:// address ending in .m3u8." onClose={onClose} footer={<button className="btn btn-primary" disabled={!form.name || !form.url} onClick={() => void save()}>Add stream</button>}>
      <div className="field"><label htmlFor="st-name">Name</label><input id="st-name" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div className="field"><label htmlFor="st-url">Stream address</label><input id="st-url" className="input" inputMode="url" placeholder="https://example.com/live/playlist.m3u8" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
      <FallbackSelect images={images} value={form.fallback} onChange={(fallback) => setForm({ ...form, fallback })} />
    </Sheet>
  );
}

function ConnectSheet({ onClose, onSaved }: { onClose: () => void; onSaved: (p: Provider) => void }) {
  const org = useOrg();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', serverUrl: '', username: '', password: '', rights: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await api<Provider>('POST', orgPath(org, '/stream-providers'), { name: form.name, serverUrl: form.serverUrl, username: form.username, password: form.password, rightsConfirmed: form.rights });
      toast('Provider connected');
      onSaved(p);
    } catch (e) {
      setError((e as ApiFailure).message);
    } finally {
      setBusy(false);
    }
  };
  const ready = form.name && form.serverUrl && form.username && form.password && form.rights && !busy;
  return (
    <Sheet title="Connect IPTV provider" subtitle="Xtream Codes login from your IPTV subscription." onClose={onClose}
      footer={<button className="btn btn-primary" disabled={!ready} onClick={() => void save()}>{busy ? 'Checking with provider…' : 'Connect'}</button>}>
      <form onSubmit={(e) => { e.preventDefault(); if (ready) void save(); }} autoComplete="off">
        <div className="field"><label htmlFor="pv-name">Name</label><input id="pv-name" className="input" placeholder="Store TV" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="field"><label htmlFor="pv-server">Server address</label><input id="pv-server" className="input" inputMode="url" placeholder="https://iptv.example.com" value={form.serverUrl} onChange={(e) => setForm({ ...form, serverUrl: e.target.value })} aria-describedby="pv-server-hint" /><span id="pv-server-hint" className="subtle">Must start with https://. Include the port if the provider gave one.</span></div>
        <div className="field"><label htmlFor="pv-user">Username</label><input id="pv-user" className="input" autoComplete="off" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
        <div className="field"><label htmlFor="pv-pass">Password</label><input id="pv-pass" className="input" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        <label className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
          <input type="checkbox" checked={form.rights} onChange={(e) => setForm({ ...form, rights: e.target.checked })} style={{ marginTop: 4 }} />
          <span>I confirm this subscription is licensed for showing its channels in our business locations (public performance). Residential IPTV plans usually are not.</span>
        </label>
        <p className="subtle" style={{ marginTop: 12 }}>LumaCast checks the login with the provider before saving, stores it encrypted, and never shows it again. Players receive a channel address only when they start playing it.</p>
        {error && <p className="error" role="alert">{error}</p>}
      </form>
    </Sheet>
  );
}

function CredentialsSheet({ provider, onClose, onSaved }: { provider: Provider; onClose: () => void; onSaved: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      await api('PATCH', orgPath(org, `/stream-providers/${provider.id}`), { ...(form.username ? { username: form.username } : {}), password: form.password });
      toast('Login updated');
      onSaved();
    } catch (e) {
      setError((e as ApiFailure).message);
    }
  };
  return (
    <Sheet title={`Change login for ${provider.name}`} subtitle="The new login is checked with the provider before it replaces the current one." onClose={onClose}
      footer={<button className="btn btn-primary" disabled={!form.password} onClick={() => void save()}>Save</button>}>
      <div className="field"><label htmlFor="cr-user">Username (leave empty to keep)</label><input id="cr-user" className="input" autoComplete="off" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
      <div className="field"><label htmlFor="cr-pass">New password</label><input id="cr-pass" className="input" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
      {error && <p className="error" role="alert">{error}</p>}
    </Sheet>
  );
}

function ChannelsSheet({ provider, images, onClose, onAdded }: { provider: Provider; images: Asset[]; onClose: () => void; onAdded: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [fallback, setFallback] = useState(images[0]?.id ?? '');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const params = new URLSearchParams({ ...(debounced ? { q: debounced } : {}), ...(category ? { category } : {}) }).toString();
  const list = useApi<{ total: number; categories: { id: string; name: string }[]; channels: Channel[] }>(orgPath(org, `/stream-providers/${provider.id}/channels${params ? `?${params}` : ''}`), [org.id, params]);
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const add = async () => {
    try {
      await api('POST', orgPath(org, `/stream-providers/${provider.id}/channels`), { streamIds: [...picked], fallbackAssetId: fallback || null });
      toast(`${picked.size} channel${picked.size === 1 ? '' : 's'} added`);
      setPicked(new Set());
      onAdded();
      void list.reload();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  };
  return (
    <Sheet title={`${provider.name} channels`} subtitle="Live channels in HLS. Choose the ones your screens should be able to show." onClose={onClose} wide
      footer={<button className="btn btn-primary" disabled={!picked.size} onClick={() => void add()}>{picked.size ? `Add ${picked.size} channel${picked.size === 1 ? '' : 's'}` : 'Add channels'}</button>}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: 1, minWidth: 200 }}><label htmlFor="ch-q">Search</label><input id="ch-q" className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Channel name" /></div>
        <div className="field"><label htmlFor="ch-cat">Category</label>
          <select id="ch-cat" className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {list.data?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <FallbackSelect images={images} value={fallback} onChange={setFallback} />
      </div>
      {list.error && <ErrorState error={list.error} retry={list.reload} />}
      {list.loading && !list.data && <Loading />}
      {list.data && (
        <>
          <p className="subtle" aria-live="polite">{list.data.total} channel{list.data.total === 1 ? '' : 's'}{list.data.total > list.data.channels.length ? `, showing the first ${list.data.channels.length}; search to narrow` : ''}</p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '50vh', overflow: 'auto' }} aria-label="Channels">
            {list.data.channels.map((c) => (
              <li key={c.streamId} className="row" style={{ padding: '6px 4px', borderBottom: '1px solid var(--border)' }}>
                <input id={`ch-${c.streamId}`} type="checkbox" disabled={c.added} checked={c.added || picked.has(c.streamId)} onChange={() => toggle(c.streamId)} />
                <span className="thumb" style={{ width: 40, height: 24, flex: 'none' }}>{c.icon ? <img src={c.icon} alt="" referrerPolicy="no-referrer" loading="lazy" /> : <Icon name="play" size={14} />}</span>
                <label htmlFor={`ch-${c.streamId}`} style={{ flex: 1 }}>{c.name}{c.categoryName && <span className="subtle"> · {c.categoryName}</span>}</label>
                {c.added && <span className="badge plain">Added</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Sheet>
  );
}
